<template>
  <modal
    v-if="active"
    height="720px"
    width="620px"
    :title="t('Export CSV')"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${locale}/shared-report`"
  >
    <template #default>
      <div class="form-container">
        <div class="columns-container">
          <div class="header justify-space-between pb-4">
            <div class="d-flex align-items-center">
              <ui-icon icon="fas fa-eye"></ui-icon>
              <div class="show-columns ml-4">
                {{ t('Show Columns') }}
              </div>
            </div>

            <ui-checkbox
              v-model="selectAll"
              @update:model-value="toggleSelectAll"
              :label="t('Select All')"
            />
          </div>
          <ui-input
            v-model="searchColumn"
            prepend-inner-icon="fas fa-search"
            :placeholder="t('Search')"
            :hide-details="1"
          />
          <div v-show="!!filteredColumns.length" class="columns-wrapper">
            <div class="columns">
              <div class="cell" v-for="column in filteredColumns" :key="column.value">
                <ui-checkbox
                  :planLimits="column.planLimits"
                  class="mt-4"
                  :disabled="column.disabled"
                  v-model="columns[column.value]"
                  @update:model-value="handleToggle(column.value, $event)"
                  :label="reportMetricsName(column.value)"
                ></ui-checkbox>
              </div>
            </div>
          </div>
          <div v-show="!filteredColumns.length" class="ui-menu-content__list-item-container not-found">
            <ui-icon :color="$colors.gray20" icon="fa fa-search-minus"></ui-icon>
            <span class="ui-menu-content__list-item-text ml-2">{{ t('Not found') }}</span>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <section class="justify-end align-items-center xs12" style="display: flex; width: 100%">
        <ui-button
          icon="fas fa-times"
          :text="t('Cancel')"
          @click="checkClose"
        />
        <ui-button
          button-style="accent"
          icon="fas fa-file-download"
          :text="t('Export CSV')"
          @click="submit"
        />
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import md5 from 'md5';
import qs from 'qs';
import {
  getReportColumnPlanLimits,
  useColors,
  useI18n,
  useReportMetrics,
  useReportParams,
  useTableQuery,
} from '@/composables';
import { $emitter } from '@/services/emitter';
import { getReportRequestLink } from '@/views/reports/report-params-build';
import { useStore } from '@/store';
import { api } from '@/services/api/main/axios';

const emit = defineEmits<{
  close: [];
}>();

const { t, locale } = useI18n();
const reportParamsApi = useReportParams();
const tableQueryApi = useTableQuery();
const $colors = useColors();
const store = useStore();
const route = useRoute();
const reportMetrics = useReportMetrics();

const active = ref(false);
const searchColumn = ref('');
const columns = ref<Record<string, boolean>>({});
const selectAll = ref(false);
const disabledColumns = ref<Record<string, boolean>>({});
const oldHash = ref('');

const columnsList = computed(() => {
  const routeType = route.params.type as string;
  return reportMetrics.getMetricsList(route.name as string, routeType).map((item) => ({
    ...item,
    planLimits: getReportColumnPlanLimits(item),
  }));
});

watchEffect(() => {
  columnsList.value.forEach((item) => {
    if (item.disabled) {
      disabledColumns.value[item.value] = true;
    }
  });
});

const selectedColumns = computed(() =>
  Object.entries(columns.value).reduce<string[]>((accumulator, [key, value]) => {
    if (value) {
      accumulator.push(key);
    }
    return accumulator;
  }, []),
);

const filteredColumns = computed(() => {
  const query = (searchColumn.value || '').toLowerCase();
  return columnsList.value.filter((column) => {
    const label = (column.text || reportMetrics.reportMetricsName(column.value) || column.value || '')
      .toString()
      .toLowerCase();
    return label.includes(query);
  });
});

const groupBy = computed(() => {
  const reportParams = reportParamsApi.getReportParams();
  const treeLevels =
    (tableQueryApi.getTableQueryForCurrentTab().treeLevels as string[])?.filter((item) => !!item) || [];
  return [reportParams.groupBy, ...treeLevels];
});

const getCurrentHash = () => md5(JSON.stringify({ ...columns.value }));

const setSelectedColumns = (selectedColumnKeys: string[]) => {
  const additionalMainColumns = reportMetrics.additionalMainColumns();
  columns.value = columnsList.value.reduce<Record<string, boolean>>((accumulator, { value }) => {
    accumulator[value] =
      selectedColumnKeys.includes(value) ||
      additionalMainColumns.findIndex((item) => item.value === value) !== -1;
    return accumulator;
  }, {});
};

const reportMetricsName = (key: string) => reportMetrics.reportMetricsName(key);

const close = () => {
  emit('close');
};

const checkClose = () => {
  if (oldHash.value !== getCurrentHash()) {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: { onConfirm: () => close() },
    });
  } else {
    close();
  }
};

const toggleSelectAll = (value: unknown) => {
  const checked = value === true;
  columnsList.value.forEach(({ value: columnKey }) => {
    if (disabledColumns.value[columnKey]) return;
    columns.value[columnKey] = checked;
  });
};

const handleToggle = (key: string, value: unknown) => {
  columns.value[key] = value === true;
  selectAll.value = selectedColumns.value.length === Object.keys(columns.value).length;
};

const submit = () => {
  const exportParams = tableQueryApi.tableExportParams.value;

  window.location.href = api.getUri({
    method: 'GET',
    url: `${api.defaults.baseURL}${getReportRequestLink(route.name)}`,
    params: {
      ...exportParams,
      groupBy: groupBy.value,
      export: 'csv',
      orderBy: selectedColumns.value.includes(exportParams.orderBy as string)
        ? exportParams.orderBy
        : selectedColumns.value[0],
      accesstoken: store.authToken,
      columns: selectedColumns.value,
      formatResponse: 0,
      presetId: reportParamsApi.activePreset.value,
    },
    paramsSerializer: (params: object) => qs.stringify(params, { arrayFormat: 'repeat' }),
  });
  close();
};

onMounted(() => {
  const selectedColumnKeys = tableQueryApi.tableExportParams.value.columns as string[];
  setSelectedColumns(selectedColumnKeys);
  selectAll.value = selectedColumns.value.length === Object.keys(columns.value).length;
  searchColumn.value = '';
  active.value = true;

  nextTick(() => {
    oldHash.value = getCurrentHash();
  });
});
</script>

<style lang="scss" scoped>
  .tooltip-container {
    padding: 7px;
    border-radius: 5px;

    .info-text {
      color: var(--color-graphite);
    }
  }

  .tooltip-content {
    height: 100%;
    cursor: default;

    .label {
      text-transform: capitalize;
      color: var(--color-green);
      border-bottom: 1px dashed;
    }
  }

  .columns-container {
    height: 100%;
    min-height: 200px;
    display: flex;
    flex-direction: column;
  }

  .form-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  }

  .columns-wrapper {
    margin-top: 5px;
    margin-left: -8px;
    overflow: hidden;
  }

  .not-found {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .columns {
    padding-bottom: 5px;
    padding-left: 8px;
    grid-gap: 5px 10px;
    display: grid;
    overflow-y: auto;
    height: 100%;
    grid-template-columns: repeat(2, minmax(100px, 1fr));

    @media (max-width: 550px) {
      grid-template-columns: 100%;
    }

    .cell {
      word-break: break-all;

      :deep(.v-input--selection-controls__input) {
        align-items: flex-start;
        top: 1px;
      }

      :deep(.v-input__slot) {
        align-items: flex-start;
      }

      :deep(.v-label) {
        display: block;
        top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      :deep(.v-input__control) {
        width: 100%;
      }
    }
  }

  .period-picker {
    margin: 0 0 15px;
    width: 100%;
  }

  .ttl {
    display: flex;
    align-items: start;
    margin-top: 4px;

    .input {
      width: 65px;
      input {
        padding: 0 15px !important;
      }
    }

    .select {
      margin-left: 5px;
      width: 100px;
    }
  }

  .report-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header {
    display: flex;
    position: relative;
    align-items: center;
  }

  .label {
    font-size: 13px;
    color: var(--color-graphite);
  }

  .show-columns {
    font-size: 13px;
    font-weight: bold;
    color: var(--color-graphite);
  }
</style>
